import PropTypes from 'prop-types';

/**
 * Button component using Preline/Tailwind styling
 *
 * A flexible button component with multiple variants, sizes, and icon support.
 * Uses Preline's button classes for consistent styling across the app.
 *
 * @param {Object} props
 * @param {string} [props.variant='primary'] - Button style variant
 * @param {string} [props.size='md'] - Button size
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.disabled] - Whether button is disabled
 * @param {React.ReactNode} [props.icon] - Icon to display (e.g., Iconify Icon component)
 * @param {string} [props.iconPosition='left'] - Icon position ('left' or 'right')
 * @param {React.ReactNode} props.children - Button content
 * @param {string} [props.type='button'] - Button type attribute
 * @param {Function} [props.onClick] - Click handler
 * @param {Object} [props...rest] - Any other HTML button attributes
 *
 * @example
 * <Button variant="primary" size="md" onClick={handleClick}>
 *   Save Changes
 * </Button>
 *
 * @example
 * <Button variant="danger" icon={<Icon icon="lucide:trash" />}>
 *   Delete
 * </Button>
 */
export function Button({
  variant = 'primary',
  size = 'md',
  className = '',
  disabled = false,
  icon = null,
  iconPosition = 'left',
  children,
  type = 'button',
  onClick,
  ...rest
}) {
  // Base button classes
  const baseClasses = 'inline-flex items-center justify-center gap-x-2 font-medium rounded-xl focus:outline-none transition-all cursor-pointer';

  // Variant classes
  const variantClasses = {
    primary: 'bg-primary text-white hover:bg-primary-dark focus:bg-primary-dark disabled:opacity-50 disabled:pointer-events-none',
    secondary: 'bg-white text-default border border-border-strong hover:bg-brand/4 focus:bg-hover-subtle disabled:opacity-50 disabled:pointer-events-none',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:bg-red-700 disabled:opacity-50 disabled:pointer-events-none',
    ghost: 'text-default hover:bg-hover-muted focus:bg-hover-muted disabled:opacity-50 disabled:pointer-events-none',
    'brand-subtle': 'bg-brand/10 text-brand border border-transparent hover:bg-brand/20 focus:bg-brand/20 disabled:opacity-50 disabled:pointer-events-none',
    'ghost-brand': 'text-brand border border-transparent hover:bg-brand/10 focus:bg-brand/10 disabled:opacity-50 disabled:pointer-events-none',
  };

  // Size classes
  const sizeClasses = {
    sm: 'text-sm py-2 px-3',
    md: 'text-sm py-2.5 px-4',
    lg: 'text-base py-3 px-5',
  };

  // Icon size classes
  const iconSizeClasses = {
    sm: 'size-4',
    md: 'size-4',
    lg: 'size-5',
  };

  const classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`.trim();

  return (
    <button
      type={type}
      className={classes}
      disabled={disabled}
      onClick={onClick}
      {...rest}
    >
      {icon && iconPosition === 'left' && (
        <span className={iconSizeClasses[size]}>
          {icon}
        </span>
      )}
      {children}
      {icon && iconPosition === 'right' && (
        <span className={iconSizeClasses[size]}>
          {icon}
        </span>
      )}
    </button>
  );
}

Button.propTypes = {
  variant: PropTypes.oneOf(['primary', 'secondary', 'danger', 'ghost', 'brand-subtle', 'ghost-brand']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  icon: PropTypes.node,
  iconPosition: PropTypes.oneOf(['left', 'right']),
  children: PropTypes.node.isRequired,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  onClick: PropTypes.func,
};
